import React from 'react';
import { reviewAegisAiHallucinationRisks } from '../../../Services/AegisAiDocumentsService';

const IDLE = { status: 'idle', issues: [], details: null, error: '' };
// Absorbs quick successive changes (e.g. unticking several website pages) into one check.
const DEBOUNCE_MS = 500;

// Checks `text` for hallucination risks whenever it changes; a newer text aborts the older check.
// Issues from the previous check stay listed while the next one runs.
export default function useHallucinationReview(token, text) {
    const [state, setState] = React.useState(IDLE);
    const [attempt, setAttempt] = React.useState(0);

    React.useEffect(() => {
        if (!token || !String(text || '').trim()) {
            setState(IDLE);
            return undefined;
        }
        const controller = new AbortController();
        setState((current) => ({ ...current, status: 'checking', error: '' }));
        const timer = setTimeout(async () => {
            try {
                // Embedded document images are only base64 data to the check, so they are left out.
                const response = await reviewAegisAiHallucinationRisks(token, text.replace(/<img\b[^>]*>/gi, ''), controller.signal);
                setState(response?.is_success
                    ? { status: 'done', issues: response.data?.Issues || [], details: response.data, error: '' }
                    : { ...IDLE, status: 'error', error: response?.message || 'Could not check for hallucination risks' });
            } catch (error) {
                if (error.name !== 'AbortError') setState({ ...IDLE, status: 'error', error: 'Could not check for hallucination risks' });
            }
        }, DEBOUNCE_MS);
        return () => {
            clearTimeout(timer);
            controller.abort();
        };
    }, [token, text, attempt]);

    const retry = React.useCallback(() => setAttempt((count) => count + 1), []);
    return { ...state, retry };
}
